/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useEffect, useRef } from '@web-stories-wp/react';
import { useSnackbar } from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { useMedia3pApi } from './api';
import { PROVIDERS } from './providerConfiguration';

/**
 * @typedef {Object} FetchMediaEffectParams
 * @property {number} retries
 * @property {string} provider provider name
 * @property {import('./typedefs').MediaProvider} selectedProvider current selected provider
 * @property {string} folder current search term for folder
 * @property {string} player current search term for player
 * @property {string} searchTerm current search term
 * @property {string} selectedCategoryId current category id
 * @property {string} tag current search term for tags
 * @property {string} pageToken current page token
 * @property {boolean} isActive is the provider active
 * @property {boolean} isMediaLoading is the media loading
 * @property {boolean} isMediaLoaded is media loaded
 * @property {import('./typedefs').FetchMediaStartFn} fetchMediaStart oction
 * dispatched when fetch media process starts.
 * @property {import('./typedefs').FetchMediaSuccessFn} fetchMediaSuccess action
 * dispatched when fetching media has been a success
 * @property {import('./typedefs').FetchMediaErrorFn} fetchMediaError action
 * dispatched when fetching media returns an error
 */

/**
 * The side effect that fetches the media from the backend.
 *
 * @param {FetchMediaEffectParams} obj required actions and parameters
 */
export default function useFetchMediaEffect({
  retries,
  provider,
  selectedProvider,
  folder,
  player,
  searchTerm,
  tag,
  selectedCategoryId,
  pageToken,
  isActive,
  isMediaLoading,
  isMediaLoaded,
  fetchMediaStart,
  fetchMediaSuccess,
  fetchMediaError,
  hasError,
}) {
  const {
    actions: { listMedia },
  } = useMedia3pApi();

  const { showSnackbar } = useSnackbar();

  const previousPropsRef = useRef();
  const latestRequestRef = useRef(0);

  useEffect(() => {
    // Previous props are read from current, and stored to current after that.
    // This allows us to get the previous values and compare for changes.
    const previousProps = previousPropsRef.current;
    previousPropsRef.current = {
      retries,
      selectedProvider,
      folder,
      pageToken,
      player,
      searchTerm,
      selectedCategoryId,
      tag,
      isActive,
      isMediaLoading,
      isMediaLoaded,
      hasError,
    };

    async function fetch() {
      if (hasError) return;
      // A newer request (query, page or library change) supersedes this one.
      const request = ++latestRequestRef.current;
      fetchMediaStart({ provider, pageToken });
      try {
        const { media, nextPageToken } = await listMedia({
          mediaProviderId: selectedProvider.mediaProviderId,
          provider,
          filter: {
            contentType: PROVIDERS[provider].contentTypeFilter,
            folder: selectedCategoryId ? null : folder,
            player: selectedCategoryId ? null : player,
            searchTerm: selectedCategoryId ? null : searchTerm,
            tag: selectedCategoryId ? null : tag,
            categoryId: selectedCategoryId,
          },
          pageToken,
        });
        if (request !== latestRequestRef.current) return;
        fetchMediaSuccess({
          provider,
          media,
          pageToken,
          nextPageToken,
        });
      } catch (e) {
        if (request !== latestRequestRef.current) return;
        fetchMediaError({ provider, pageToken });
        showSnackbar({
          message: PROVIDERS[provider].fetchMediaErrorMessage,
          dismissable: true,
        });
      }
    }

    // If we switched provider tab, and we already had loaded a page there, we
    // don't load media again.
    const somethingChanged =
      previousProps &&
      (pageToken != previousProps.pageToken ||
        isActive != previousProps.isActive ||
        retries != previousProps.retries ||
        folder != previousProps.folder ||
        player !== previousProps.player ||
        searchTerm != previousProps.searchTerm ||
        tag != previousProps.tag ||
        selectedCategoryId != previousProps.selectedCategoryId ||
        hasError != previousProps.hasError ||
        selectedProvider?.mediaProviderId !==
          previousProps?.selectedProvider?.mediaProviderId);

    const firstFetchOrSomethingChanged =
      !previousProps ||
      (!previousProps.isMediaLoading && !previousProps.isMediaLoaded) ||
      somethingChanged;
    const hasRequiredFolder =
      !PROVIDERS[provider].requiresFolderSelection || Boolean(folder);

    if (
      provider === selectedProvider?.providerConfigurationKey &&
      firstFetchOrSomethingChanged &&
      hasRequiredFolder
    ) {
      fetch();
    }
    // We don't want to depend on previousProps, see https://blog.logrocket.com/how-to-get-previous-props-state-with-react-hooks/
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Fetch media is triggered by changes to these.
    retries,
    selectedProvider,
    pageToken,
    folder,
    player,
    searchTerm,
    selectedCategoryId,
    tag,
    // These attributes never change.
    provider,
    listMedia,
    fetchMediaError,
    fetchMediaStart,
    fetchMediaSuccess,
    showSnackbar,
    previousPropsRef,
    hasError,
  ]);
}
